import React, { useState, Fragment, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import {
  ArrowLeftIcon,
  ClockIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  ChevronUpDownIcon
} from '@heroicons/react/24/outline';
import { Combobox, Transition } from '@headlessui/react';
import { useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import { problemStateLabels, getDisplayLabel } from '../../utils/labelMappings';
import RichTextEditor from '../../components/RichTextEditor';
import { toast } from 'react-hot-toast';
import AssignmentGroupChangeConfirmModal from '../../components/AssignmentGroupChangeConfirmModal';
import { useAuth } from '../../contexts/AuthContext';
import { useIsServiceDeskUser } from '../../hooks/useIsServiceDeskUser';
import { useTicketPermissions } from '../../hooks/useTicketPermissions';
import { ASSIGNMENT_ONLY_LOCK_CLASS, buildAssignmentOnlyPayload } from '../../utils/ticketAssignmentEdit';
import { formatDateForInput, dateToUTCNoon } from '../../utils/dateHelpers';
import { fetchAssignmentGroupMembers } from '../../utils/groupMembers';
import DateTimePickerInput from '../../components/DateTimePickerInput';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { trackProblemOperation } from '../../utils/appInsights';
import { hasActualTextContent } from '../../utils/sanitize';

interface FormData {
  shortDescription: string;
  problemStatement: string;
  contactId: string;
  customerId: string;
  engagementId: string;
  resourceId: string;
  otherResourceNames: string;
  assignmentGroupId: string;
  assignedTo: string;
  impact: string;
  urgency: string;
  state: string;
  cause: string;
  solution: string;
  resolutionNotes: string;
  // Review Matrix fields
  rcaPresentedById: string;
  reportedIssue: string;
  problemDate: string;
  actualServiceImpact: string;
  briefAnalysis: string;
  rootCause: string;
  detailedAnalysis: string;
  correctiveActions: string;
  chronologyOfEvents: string;
}

// Problem State Transitions
const PROBLEM_STATE_TRANSITIONS = {
  new: ['assess_1'],
  assess_1: ['assess_2'],
  assess_2: ['root_cause_analysis'],
  root_cause_analysis: ['resolved'],
  resolved: ['closed'],
  closed: []
};

// Problem Workflow definition (matching ProblemDetail)
const problemWorkflow = [
  { id: 'new', name: 'New', icon: ClockIcon },
  { id: 'assess_1', name: 'Assess 1', icon: ExclamationTriangleIcon },
  { id: 'assess_2', name: 'Assess 2', icon: ExclamationTriangleIcon },
  { id: 'root_cause_analysis', name: 'Root Cause Analysis', icon: LightBulbIcon },
  { id: 'resolved', name: 'Resolved', icon: CheckIcon },
  { id: 'closed', name: 'Closed', icon: XMarkIcon }
];

const ProblemEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, loading: permissionsLoading } = useDynamicPermissions();
  const hasPermission = can('problems', 'update');
  const { user } = useAuth();
  const isServiceDeskUser = useIsServiceDeskUser();
  const { formatDateTime, getToday } = useDateFormatter();

  const isRichTextEmpty = (content: string): boolean => !hasActualTextContent(content);

  // Priority calculation function (matches backend logic)
  const calculatePriorityFromImpactUrgency = (impact: string, urgency: string): string => {
    if (!impact || !urgency) return 'moderate_3'; // Default to Moderate

    const impactNum = parseInt(impact.split('_')[1]);
    const urgencyNum = parseInt(urgency.split('_')[1]);

    if (impactNum === 1 && urgencyNum === 1) return 'critical_1';
    if ((impactNum === 1 && urgencyNum === 2) || (impactNum === 2 && urgencyNum === 1)) return 'high_2';
    if ((impactNum === 1 && urgencyNum === 3) || (impactNum === 2 && urgencyNum === 2) || (impactNum === 3 && urgencyNum === 1)) return 'moderate_3';
    if ((impactNum === 2 && urgencyNum === 3) || (impactNum === 3 && urgencyNum === 2)) return 'low_4';
    if (impactNum === 3 && urgencyNum === 3) return 'planning_5';

    return 'moderate_3'; // Default to Moderate
  };

  // Fetch problem data
  const { data: problem, isLoading } = useQuery({
    queryKey: ['problem', id],
    queryFn: async () => {
      const response = await api.get(`/cases/${id}`);
      return response.data;
    },
    enabled: !!id
  });

  const { isAssignmentOnlyEditMode } = useTicketPermissions(problem, 'problems');

  const [formData, setFormData] = useState<FormData>({
    shortDescription: '',
    problemStatement: '',
    contactId: '',
    customerId: '',
    engagementId: '',
    resourceId: '',
    otherResourceNames: '',
    assignmentGroupId: '',
    assignedTo: '',
    impact: 'medium_2',
    urgency: 'medium_2',
    state: 'new',
    cause: '',
    solution: '',
    resolutionNotes: '',
    // Review Matrix fields
    rcaPresentedById: '',
    reportedIssue: '',
    problemDate: '',
    actualServiceImpact: '',
    briefAnalysis: '',
    rootCause: '',
    detailedAnalysis: '',
    correctiveActions: '',
    chronologyOfEvents: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedResourceIdValue, setSelectedResourceIdValue] = useState<string>('');
  const [isOtherResource, setIsOtherResource] = useState<boolean>(false);
  const [callerQuery, setCallerQuery] = useState('');
  const [assignmentGroupQuery, setAssignmentGroupQuery] = useState('');
  const [assignedToQuery, setAssignedToQuery] = useState('');
  const [rcaPresentedByQuery, setRcaPresentedByQuery] = useState('');

  // Assignment group change confirmation modal state
  const [groupChangeConfirmModal, setGroupChangeConfirmModal] = useState<{
    isOpen: boolean;
    newGroupId: string | null;
    newGroupName: string;
    pendingData: any;
  }>({
    isOpen: false,
    newGroupId: null,
    newGroupName: '',
    pendingData: null
  });

  // Debug: Monitor errors state changes
  useEffect(() => {
    console.log('Errors state changed:', errors);
  }, [errors]);

  // Initialize form data when problem is loaded
  React.useEffect(() => {
    if (problem) {
      setFormData({
        shortDescription: problem.shortDescription || '',
        problemStatement: problem.problemStatement || '',
        contactId: problem.contactId || problem.User_Case_contactIdToUser?.id || '',
        customerId: problem.customerId || problem.Customer?.id || '',
        engagementId: problem.engagementId || problem.Engagement?.id || '',
        resourceId: problem.resourceId || problem.Resource?.id || problem.EngagementResource?.id || '',
        otherResourceNames: problem.otherResourceNames || '',
        assignmentGroupId: problem.assignmentGroupId || problem.Group?.id || '',
        assignedTo: problem.assignedToId || problem.User_Case_assignedToIdToUser?.id || '',
        impact: problem.impact || 'medium_2',
        urgency: problem.urgency || 'medium_2',
        state: problem.state || 'new',
        cause: problem.cause || '',
        solution: problem.solution || '',
        resolutionNotes: problem.resolution || '',
        // Review Matrix fields
        rcaPresentedById: problem.rcaPresentedById || problem.RCAPresenter?.id || '',
        reportedIssue: problem.reportedIssue || '',
        problemDate: formatDateForInput(problem.problemDate),
        actualServiceImpact: problem.actualServiceImpact || '',
        briefAnalysis: problem.briefAnalysis || '',
        rootCause: problem.rootCause || '',
        detailedAnalysis: problem.detailedAnalysis || '',
        correctiveActions: problem.correctiveActions || '',
        chronologyOfEvents: problem.chronologyOfEvents || ''
      });
      // Initialize "Others" resource state
      const hasOtherResource = !problem.resourceId && !!problem.otherResourceNames;
      setIsOtherResource(hasOtherResource);
      setSelectedResourceIdValue(problem.Resource?.resourceId || problem.EngagementResource?.resourceId || '');
    }
  }, [problem]);

  // Fetch contacts for Reported By dropdown - filtered by customer
  const { data: contactsData } = useQuery({
    queryKey: ['contacts', formData.customerId, callerQuery],
    queryFn: async () => {
      if (!formData.customerId) return { users: [] };
      const params = new URLSearchParams({
        class: 'contact',
        customerId: formData.customerId,
        limit: '100'
      });
      if (callerQuery) {
        params.append('search', callerQuery);
      }
      const response = await api.get(`/users?${params.toString()}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Fetch all users for RCA Presented By dropdown
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await api.get('/users?limit=1000');
      return response.data;
    }
  });

  // Fetch group members for assignment dropdown - only when assignment group is selected
  const { data: groupMembersData } = useQuery({
    queryKey: ['group-members', formData.assignmentGroupId, formData.customerId],
    queryFn: async () => {
      if (!formData.assignmentGroupId) return [];
      return fetchAssignmentGroupMembers(formData.assignmentGroupId, formData.customerId || null);
    },
    enabled: !!formData.assignmentGroupId
  });

  // Fetch assignment groups for dropdown (only groups with type 'assignment')
  const { data: assignmentGroupsData } = useQuery({
    queryKey: ['assignment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups/assignment-groups');
      return response.data;
    }
  });

  // Fetch customers for dropdown
  useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    }
  });

  // Fetch engagements for dropdown - only when customer is selected
  useQuery({
    queryKey: ['engagements', formData.customerId],
    queryFn: async () => {
      if (!formData.customerId) return { engagements: [] };
      const response = await api.get(`/engagements?customerId=${formData.customerId}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Fetch resources for dropdown - only when engagement is selected
  useQuery({
    queryKey: ['resources', formData.engagementId],
    queryFn: async () => {
      if (!formData.engagementId) return { resources: [] };
      const response = await api.get(`/resources?engagementId=${formData.engagementId}`);
      return response.data;
    },
    enabled: !!formData.engagementId
  });


  const updateProblemMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.put(`/cases/${id}`, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['problem', id] });
      queryClient.invalidateQueries({ queryKey: ['problems'] });

      // Log Problem Details Modified
      if (user && problem) {
        trackProblemOperation(
          'Problem Details - Edit',
          user.email || user.firstName || 'Unknown',
          {
            action: 'Problem Details Modified',
            problemNumber: problem.number || problem.id || id || 'Unknown',
          },
          {
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role,
            roleType: user.roleType,
            roleLevel: user.roleLevel,
            userClass: user.class,
            customerId: user.customerId,
            customerName: user.customerName,
            engagementId: user.engagementId,
            engagementName: user.engagementName,
            isServiceDeskUser: isServiceDeskUser,
          }
        );
      }

      // Check if this was a confirmed assignment group change - if so, redirect to list
      if (groupChangeConfirmModal.isOpen) {
        setGroupChangeConfirmModal({
          isOpen: false,
          newGroupId: null,
          newGroupName: '',
          pendingData: null
        });

        // Invalidate list queries to ensure fresh data on redirect
        queryClient.invalidateQueries({ queryKey: ['problems'] });
        queryClient.invalidateQueries({ queryKey: ['cases'] });

        toast.success('Problem updated successfully. Redirecting to list...');
        setTimeout(() => navigate('/problems'), 500);
        return;
      }

      // Normal flow - redirect back to detail page
      navigate(`/problems/${id}`);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update problem';
      setErrors({ submit: message });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isAssignmentOnlyEditMode) {
      if (!formData.assignmentGroupId) {
        setErrors({ assignmentGroupId: 'Assignment Group is required' });
        return;
      }

      setErrors({});
      updateProblemMutation.mutate(buildAssignmentOnlyPayload(formData));
      return;
    }

    // Validate form
    const newErrors: Record<string, string> = {};
    if (isRichTextEmpty(formData.shortDescription)) {
      newErrors.shortDescription = 'Short Description is required';
    }

    console.log('Problem Statement value:', formData.problemStatement);
    console.log('Is Rich Text Empty:', isRichTextEmpty(formData.problemStatement));

    if (isRichTextEmpty(formData.problemStatement)) {
      newErrors.problemStatement = 'Problem Statement is required';
      console.log('Problem Statement error set');
    }
    if (!formData.customerId) {
      newErrors.customerId = 'Customer is required';
    }
    if (!formData.engagementId) {
      newErrors.engagementId = 'Engagement is required';
    }
    // Resource is readonly in edit mode - no validation needed
    if (!formData.rcaPresentedById) {
      newErrors.rcaPresentedById = 'RCA Presented By is required';
    }
    if (!formData.problemDate) {
      newErrors.problemDate = 'Problem Date is required';
    } else {
      // Check if problem date is in the future using date-only comparison (avoids timezone issues)
      const selectedDateStr = formData.problemDate.split('T')[0];
      const todayStr = getToday();
      if (selectedDateStr > todayStr) {
        newErrors.problemDate = 'Problem Date cannot be in the future';
      }
    }
    if (isRichTextEmpty(formData.reportedIssue)) {
      newErrors.reportedIssue = 'Reported Issue is required';
    }
    if (isRichTextEmpty(formData.actualServiceImpact)) {
      newErrors.actualServiceImpact = 'Actual Service Impact is required';
    }
    if (isRichTextEmpty(formData.briefAnalysis)) {
      newErrors.briefAnalysis = 'Brief Analysis is required';
    }
    if (isRichTextEmpty(formData.rootCause)) {
      newErrors.rootCause = 'Root Cause is required';
    }
    if (isRichTextEmpty(formData.detailedAnalysis)) {
      newErrors.detailedAnalysis = 'Detailed Analysis is required';
    }
    if (isRichTextEmpty(formData.correctiveActions)) {
      newErrors.correctiveActions = 'Corrective/Mitigate Actions is required';
    }
    if (isRichTextEmpty(formData.chronologyOfEvents)) {
      newErrors.chronologyOfEvents = 'Chronology of Events is required';
    }
    if (!formData.assignmentGroupId) {
      newErrors.assignmentGroupId = 'Assignment Group is required';
    }

    if (Object.keys(newErrors).length > 0) {
      console.log('Validation errors found:', newErrors);
      setErrors(newErrors);
      console.log('Errors state updated');
      // Scroll to the first error field
      const firstErrorField = Object.keys(newErrors)[0];
      const errorElement = document.getElementById(firstErrorField);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
      return;
    }

    // Calculate priority from impact and urgency
    const calculatedPriority = calculatePriorityFromImpactUrgency(formData.impact, formData.urgency);
    const submitData: any = {
      shortDescription: formData.shortDescription,
      problemStatement: formData.problemStatement,
      impact: formData.impact,
      urgency: formData.urgency,
      priority: calculatedPriority,
      state: formData.state
      // Note: cause, solution, resolutionNotes, and implementedDate are managed through workflow transitions
    };

    if (formData.customerId) submitData.customerId = formData.customerId;
    if (formData.engagementId) submitData.engagementId = formData.engagementId;
    // Resource is readonly in edit mode - not included in submission

    // Always send assignedTo (backend expects 'assignedTo', not 'assignedToId')
    submitData.assignedTo = formData.assignedTo || null;
    if (formData.assignmentGroupId) submitData.assignmentGroupId = formData.assignmentGroupId;
    if (formData.contactId) submitData.contactId = formData.contactId;

    // Review Matrix fields (always include - these are mandatory)
    submitData.rcaPresentedById = formData.rcaPresentedById;
    submitData.reportedIssue = formData.reportedIssue;
    if (formData.problemDate) {
      // Convert date string to ISO 8601 datetime format at UTC noon
      // This prevents timezone-related date shifting issues
      submitData.problemDate = dateToUTCNoon(formData.problemDate);
    }
    submitData.actualServiceImpact = formData.actualServiceImpact;
    submitData.briefAnalysis = formData.briefAnalysis;
    submitData.rootCause = formData.rootCause;
    submitData.detailedAnalysis = formData.detailedAnalysis;
    submitData.correctiveActions = formData.correctiveActions;
    submitData.chronologyOfEvents = formData.chronologyOfEvents;

    console.log('=== PROBLEM UPDATE DEBUG ===');
    console.log('formData.assignedTo:', formData.assignedTo);
    console.log('submitData:', submitData);
    console.log('submitData.assignedTo:', submitData.assignedTo);
    console.log('=== END DEBUG ===');

    // Check if assignment group is changing and user is not a member of the new group
    // Only check for regular users (user/agent roles), not for super_admin/admin/manager
    // Service Desk users can access all tickets, so skip the warning for them
    const userRole = user?.role;
    const isGlobalAccessRole = userRole === 'super_admin' || userRole === 'admin' || userRole === 'manager';

    if (submitData.assignmentGroupId && problem && submitData.assignmentGroupId !== problem.assignmentGroupId && !isGlobalAccessRole && !isServiceDeskUser) {
      const newGroupId = submitData.assignmentGroupId;
      // Check if user is a member of the new group by checking group members
      // groupMembersData is already fetched for the selected assignment group
      const newGroupMembers = groupMembersData || [];
      const isUserMemberOfNewGroup = newGroupMembers.some((member: any) =>
        member.id === user?.id || member._id === user?.id
      );

      if (!isUserMemberOfNewGroup) {
        const newGroup = (assignmentGroupsData || []).find((group: any) =>
          group.id === newGroupId || group._id === newGroupId
        );
        const newGroupName = newGroup?.name || 'Unknown Group';

        setGroupChangeConfirmModal({
          isOpen: true,
          newGroupId,
          newGroupName,
          pendingData: submitData
        });
        return;
      }
    }

    updateProblemMutation.mutate(submitData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
      };

      // Clear dependent fields when parent fields change
      if (name === 'customerId') {
        updated.engagementId = '';
        updated.resourceId = '';
        updated.contactId = '';
      }
      if (name === 'engagementId') {
        updated.resourceId = '';
        updated.contactId = '';
      }
      if (name === 'assignmentGroupId') {
        updated.assignedTo = '';
      }

      return updated;
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Assignment group change confirmation handlers
  const handleConfirmGroupChange = () => {
    if (groupChangeConfirmModal.pendingData) {
      updateProblemMutation.mutate(groupChangeConfirmModal.pendingData);
    }
  };

  const handleCancelGroupChange = () => {
    setGroupChangeConfirmModal({
      isOpen: false,
      newGroupId: null,
      newGroupName: '',
      pendingData: null
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading problem...</p>
        </div>
      </div>
    );
  }

  if (!problem) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Problem not found.</p>
        </div>
      </div>
    );
  }


  // Show loading while checking permissions
  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check permission
  if (!hasPermission) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          You don't have permission to update problems.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-8 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate(`/problems/${id}`)}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Problem
          </button>
        </div>

        {/* Problem Workflow Progress - Top Section */}
        <div className="bg-white shadow rounded-lg mb-6 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Problem Workflow</h3>
          <nav aria-label="Progress">
            <ol className="flex items-center">
              {problemWorkflow.map((step, stepIdx) => {
                const currentState = problem.problemState || 'new';
                const currentIdx = problemWorkflow.findIndex(s => s.id === currentState);
                const Icon = step.icon;
                const isComplete = stepIdx < currentIdx;
                const isCurrent = stepIdx === currentIdx;
                const isCancelled = currentState === 'cancelled';

                return (
                  <li key={step.id} className={stepIdx !== problemWorkflow.length - 1 ? 'flex-1' : ''}>
                    <div className="flex items-center">
                      <div
                        className={`relative flex h-10 w-10 items-center justify-center rounded-full ${
                          isComplete ? 'bg-indigo-600' :
                          isCurrent ? 'bg-indigo-600' :
                          'bg-gray-300'
                        } ${isCancelled ? 'opacity-50' : ''}`}
                      >
                        <Icon className={`h-6 w-6 ${
                          isComplete || isCurrent ? 'text-white' : 'text-gray-600'
                        }`} />
                      </div>
                      {stepIdx !== problemWorkflow.length - 1 && (
                        <div className={`ml-4 flex-1 h-0.5 ${
                          isComplete ? 'bg-indigo-600' : 'bg-gray-300'
                        }`} />
                      )}
                    </div>
                    <div className="mt-2">
                      <span className={`text-sm font-medium ${
                        isComplete || isCurrent ? 'text-gray-900' : 'text-gray-500'
                      }`}>
                        {step.name}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ol>
          </nav>

          {/* Current Status and Change To Section */}
          {(() => {
            const currentState = problem.problemState || 'new';
            const getAvailableTransitions = (currentState: string): string[] => {
              return PROBLEM_STATE_TRANSITIONS[currentState as keyof typeof PROBLEM_STATE_TRANSITIONS] || [];
            };
            const availableTransitions = getAvailableTransitions(currentState);

            // Check for missing mandatory fields
            const missingFields: string[] = [];

            if (!formData.assignedTo) {
              missingFields.push('Assigned To');
            }
            if (!formData.rcaPresentedById) {
              missingFields.push('RCA Presented By');
            }
            if (!formData.problemDate) {
              missingFields.push('Problem Date');
            }
            if (!formData.reportedIssue?.trim()) {
              missingFields.push('Reported Issue');
            }
            if (!formData.actualServiceImpact?.trim()) {
              missingFields.push('Actual Service Impact');
            }
            if (!formData.briefAnalysis?.trim()) {
              missingFields.push('Brief Analysis');
            }
            if (!formData.rootCause?.trim()) {
              missingFields.push('Root Cause');
            }
            if (!formData.detailedAnalysis?.trim()) {
              missingFields.push('Detailed Analysis');
            }
            if (!formData.correctiveActions?.trim()) {
              missingFields.push('Corrective/Mitigate Actions');
            }
            if (!formData.chronologyOfEvents?.trim()) {
              missingFields.push('Chronology of Events');
            }

            return (
              <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-600">Current Status:</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {getDisplayLabel(currentState, problemStateLabels)}
                  </span>
                </div>

                {/* Hide "Change to" buttons if there are missing fields */}
                {missingFields.length === 0 && (availableTransitions.length > 0 || currentState !== 'cancelled') && (
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-600">Change to:</span>
                    <div className="flex gap-2">
                      {availableTransitions.map((state) => (
                        <button
                          key={state}
                          onClick={() => navigate(`/problems/${id}`)}
                          className="px-4 py-1.5 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                          title="Go to detail page to change status"
                        >
                          {getDisplayLabel(state, problemStateLabels)}
                        </button>
                      ))}
                      {currentState !== 'cancelled' && currentState !== 'closed' && currentState !== 'resolved' && (
                        <button
                          onClick={() => navigate(`/problems/${id}`)}
                          className="px-4 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded hover:bg-red-50 transition-colors"
                          title="Go to detail page to cancel"
                        >
                          Cancelled
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Mandatory Fields Warning Notification */}
        {(() => {
          const missingFields: string[] = [];

          if (!formData.assignedTo) {
            missingFields.push('Assigned To');
          }
          if (!formData.rcaPresentedById) {
            missingFields.push('RCA Presented By');
          }
          if (!formData.problemDate) {
            missingFields.push('Problem Date');
          }
          if (!formData.reportedIssue?.trim()) {
            missingFields.push('Reported Issue');
          }
          if (!formData.actualServiceImpact?.trim()) {
            missingFields.push('Actual Service Impact');
          }
          if (!formData.briefAnalysis?.trim()) {
            missingFields.push('Brief Analysis');
          }
          if (!formData.rootCause?.trim()) {
            missingFields.push('Root Cause');
          }
          if (!formData.detailedAnalysis?.trim()) {
            missingFields.push('Detailed Analysis');
          }
          if (!formData.correctiveActions?.trim()) {
            missingFields.push('Corrective/Mitigate Actions');
          }
          if (!formData.chronologyOfEvents?.trim()) {
            missingFields.push('Chronology of Events');
          }

          if (missingFields.length > 0) {
            return (
              <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md shadow-sm">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Incomplete Review Matrix
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>
                        Please provide <strong>Assigned To</strong> and complete the <strong>Review Matrix</strong> fields before moving to the next state.
                      </p>
                      <p className="mt-2">
                        <strong>Missing fields:</strong> {missingFields.join(', ')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })()}

        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h1 className="text-xl font-semibold text-gray-900">Edit Problem - {problem.number}</h1>
          </div>

          <form onSubmit={handleSubmit} className="p-8">
            {errors.submit && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800">{errors.submit}</p>
              </div>
            )}

            {isAssignmentOnlyEditMode && (
              <div className="mb-6 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                You can update Assignment Group and Assigned To for this ticket. Other fields are read-only.
              </div>
            )}

            <div className={isAssignmentOnlyEditMode ? ASSIGNMENT_ONLY_LOCK_CLASS : 'contents'}>
            {/* Row 1: Number and Opened */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Number
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={problem.number || ''}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Opened
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={problem.createdAt ? formatDateTime(problem.createdAt) : ''}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Impact and Urgency */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="impact" className="block text-sm font-medium text-gray-700 mb-1">
                      Impact *
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      id="impact"
                      name="impact"
                      value={formData.impact}
                      onChange={handleChange}
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    >
                      <option value="high_1">High</option>
                      <option value="medium_2">Medium</option>
                      <option value="low_3">Low</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="urgency" className="block text-sm font-medium text-gray-700 mb-1">
                      Urgency *
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      id="urgency"
                      name="urgency"
                      value={formData.urgency}
                      onChange={handleChange}
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    >
                      <option value="high_1">High</option>
                      <option value="medium_2">Medium</option>
                      <option value="low_3">Low</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 3: State and Priority (Auto-calculated) */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={problem?.problemState?.replace(/_/g, ' ') || 'New'}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8 capitalize"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority (Auto-calculated)
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={(() => {
                        const priority = calculatePriorityFromImpactUrgency(formData.impact, formData.urgency);
                        switch (priority) {
                          case 'critical_1': return '1 - Critical';
                          case 'high_2': return '2 - High';
                          case 'moderate_3': return '3 - Moderate';
                          case 'low_4': return '4 - Low';
                          case 'planning_5': return '5 - Planning';
                          default: return '3 - Moderate';
                        }
                      })()}
                      disabled
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 4: Customer and Engagement */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="customerId" className="block text-sm font-medium text-gray-700 mb-1">
                      Customer
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={problem?.Customer?.accountName || 'Not specified'}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="engagementId" className="block text-sm font-medium text-gray-700 mb-1">
                      Engagement
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={problem?.Engagement?.name || 'Not specified'}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 5: Contact */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="contactId" className="block text-sm font-medium text-gray-700 mb-1">
                      Contact
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.contactId}
                      onChange={(value: string | null) => {
                        setFormData({ ...formData, contactId: value || '' });
                        if (errors.contactId) {
                          setErrors(prev => ({ ...prev, contactId: '' }));
                        }
                      }}
                      disabled={!formData.customerId}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-400"
                          displayValue={(userId: string) => {
                            // First check in loaded contacts
                            const user = (contactsData?.users || []).find((u: any) => u.id === userId);
                            if (user) return `${user.firstName} ${user.lastName}`;
                            // Fallback to problem's original contact data
                            const contact = problem?.User_Case_contactIdToUser || problem?.Contact || problem?.contact;
                            if (contact && contact.id === userId) {
                              return `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.email || '';
                            }
                            return '';
                          }}
                          onChange={(event) => setCallerQuery(event.target.value)}
                          placeholder={!formData.customerId ? 'Select Customer first' : 'Search contacts...'}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setCallerQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {contactsData?.users && contactsData.users.length === 0 && callerQuery !== '' ? (
                              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                No contacts found.
                              </div>
                            ) : contactsData?.users && contactsData.users.length === 0 ? (
                              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                No contacts available for this customer.
                              </div>
                            ) : (
                              (contactsData?.users || []).map((user: any) => (
                                <Combobox.Option
                                  key={user.id}
                                  value={user.id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ active, selected }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                        {user.firstName} {user.lastName} ({user.email})
                                      </span>
                                      {selected && (
                                        <span
                                          className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                            active ? 'text-white' : 'text-indigo-600'
                                          }`}
                                        >
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))
                            )}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 6: Resource and Resource ID / Custom Resources (Readonly) */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Resource
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={isOtherResource ? 'Others (Custom)' : (problem?.Resource?.name || problem?.EngagementResource?.name || '-')}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {isOtherResource ? 'Resource Names' : 'Resource ID'}
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={isOtherResource
                        ? (formData.otherResourceNames || '-')
                        : (selectedResourceIdValue || '-')}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
            </div>

            </div>

            {/* Row 6: Assignment Group and Assigned To */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="assignmentGroupId" className="block text-sm font-medium text-gray-700 mb-1">
                      Assignment Group <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.assignmentGroupId}
                      onChange={(value: string | null) => {
                        setFormData(prev => ({ ...prev, assignmentGroupId: value || '', assignedTo: '' }));
                      }}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                          displayValue={(groupId: string) => {
                            const group = (assignmentGroupsData || []).find((g: any) => (g.id || g._id) === groupId);
                            if (group) return group.name;
                            // Fallback to problem's original assignment group data
                            if (problem?.Group && (problem.Group.id === groupId || problem.Group._id === groupId)) {
                              return problem.Group.name || '';
                            }
                            return '';
                          }}
                          onChange={(event) => setAssignmentGroupQuery(event.target.value)}
                          placeholder="Search assignment groups..."
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setAssignmentGroupQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {(assignmentGroupsData || [])
                              .filter((group: any) => group && (group.id || group._id) && group.name)
                              .filter((group: any) =>
                                assignmentGroupQuery === '' ||
                                group.name.toLowerCase().includes(assignmentGroupQuery.toLowerCase())
                              )
                              .map((group: any) => (
                                <Combobox.Option
                                  key={group.id || group._id}
                                  value={group.id || group._id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ active, selected }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                        {group.name}
                                      </span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700 mb-1">
                      Assigned To
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.assignedTo}
                      onChange={(value: string | null) => {
                        setFormData(prev => ({ ...prev, assignedTo: value || '' }));
                      }}
                      disabled={!formData.assignmentGroupId}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-400"
                          displayValue={(memberId: string) => {
                            const member = (groupMembersData || []).find((m: any) => m.id === memberId);
                            if (member) return `${member.firstName} ${member.lastName}`;
                            // Fallback to problem's original assigned to data
                            const assignedTo = problem?.User_Case_assignedToIdToUser || problem?.AssignedTo;
                            if (assignedTo && assignedTo.id === memberId) {
                              return `${assignedTo.firstName || ''} ${assignedTo.lastName || ''}`.trim() || assignedTo.email || '';
                            }
                            return '';
                          }}
                          onChange={(event) => setAssignedToQuery(event.target.value)}
                          placeholder={!formData.assignmentGroupId ? 'Select Assignment Group first' : 'Search assignees...'}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setAssignedToQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {(groupMembersData || [])
                              .filter((member: any) => member && member.id && member.firstName)
                              .filter((member: any) =>
                                assignedToQuery === '' ||
                                `${member.firstName} ${member.lastName}`.toLowerCase().includes(assignedToQuery.toLowerCase())
                              )
                              .map((member: any) => (
                                <Combobox.Option
                                  key={member.id}
                                  value={member.id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ active, selected }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                        {member.firstName} {member.lastName}
                                      </span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                  </div>
                </div>
              </div>
            </div>


            <div className={isAssignmentOnlyEditMode ? ASSIGNMENT_ONLY_LOCK_CLASS : 'contents'}>
            {/* Short Description (full width) */}
            <div className="grid grid-cols-12 gap-6 mb-6 pt-4 border-t border-gray-200">
              <div className="col-span-12">
                <div className="flex gap-4 items-start">
                  <div className="w-[16.666%] flex-shrink-0">
                    <label htmlFor="shortDescription" className="block text-sm font-medium text-gray-700">
                      Short Description <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="flex-1">
                    <RichTextEditor
                      value={formData.shortDescription || ''}
                      onChange={(value) => {
                        setFormData({ ...formData, shortDescription: value });
                        if (errors.shortDescription) {
                          setErrors(prev => ({ ...prev, shortDescription: '' }));
                        }
                      }}
                      placeholder="Brief summary of the problem"
                      minHeight="80px"
                    />
                    {errors.shortDescription && (
                      <p className="mt-1 text-sm text-red-600">{errors.shortDescription}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Problem Statement (full width) */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-12">
                <div className="flex gap-4 items-start">
                  <div className="w-[16.666%] flex-shrink-0">
                    <label htmlFor="problemStatement" className="block text-sm font-medium text-gray-700">
                      Problem Statement <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="flex-1">
                    <div id="problemStatement" className={errors.problemStatement ? 'border-2 border-red-300 rounded' : ''}>
                      <RichTextEditor
                        value={formData.problemStatement || ''}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, problemStatement: value }));
                          if (errors.problemStatement) {
                            setErrors(prev => ({ ...prev, problemStatement: '' }));
                          }
                        }}
                        placeholder="Specific problem statement for better tracking"
                        minHeight="120px"
                      />
                    </div>
                    {errors.problemStatement && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.problemStatement}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Review Matrix Section */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Review Matrix</h2>

              {/* Row 1: RCA Presented By and Problem Date */}
              <div className="grid grid-cols-12 gap-6 mb-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label htmlFor="rcaPresentedById" className="block text-sm font-medium text-gray-700 mb-1">
                        RCA Presented By <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-9">
                      <Combobox
                        value={formData.rcaPresentedById}
                        onChange={(value: string | null) => {
                          setFormData(prev => ({ ...prev, rcaPresentedById: value || '' }));
                          if (errors.rcaPresentedById) {
                            setErrors(prev => ({ ...prev, rcaPresentedById: '' }));
                          }
                        }}
                      >
                        <div className="relative">
                          <Combobox.Input
                            className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                              errors.rcaPresentedById ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                            }`}
                            displayValue={(userId: string) => {
                              const user = (usersData?.users || []).find((u: any) => (u.id || u._id) === userId);
                              if (user) return `${user.firstName} ${user.lastName}`;
                              // Fallback to problem's original RCA presenter data
                              const rcaPresenter = problem?.RCAPresenter || problem?.User_Case_rcaPresentedByIdToUser;
                              if (rcaPresenter && (rcaPresenter.id === userId || rcaPresenter._id === userId)) {
                                return `${rcaPresenter.firstName || ''} ${rcaPresenter.lastName || ''}`.trim() || rcaPresenter.email || '';
                              }
                              return '';
                            }}
                            onChange={(event) => setRcaPresentedByQuery(event.target.value)}
                            placeholder="Search users..."
                          />
                          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                          </Combobox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                            afterLeave={() => setRcaPresentedByQuery('')}
                          >
                            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              {(usersData?.users || [])
                                .filter((user: any) => user && (user.id || user._id) && user.firstName)
                                .filter((user: any) =>
                                  rcaPresentedByQuery === '' ||
                                  `${user.firstName} ${user.lastName}`.toLowerCase().includes(rcaPresentedByQuery.toLowerCase())
                                )
                                .map((user: any) => (
                                  <Combobox.Option
                                    key={user.id || user._id}
                                    value={user.id || user._id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                        active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                      }`
                                    }
                                  >
                                    {({ active, selected }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                          {user.firstName} {user.lastName}
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                            </Combobox.Options>
                          </Transition>
                        </div>
                      </Combobox>
                      {errors.rcaPresentedById && (
                        <p className="mt-1 text-sm text-red-600">{errors.rcaPresentedById}</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label htmlFor="problemDate" className="block text-sm font-medium text-gray-700 mb-1">
                        Problem Date <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-9">
                      <DateTimePickerInput
                        id="problemDate"
                        name="problemDate"
                        value={formData.problemDate}
                        onChange={(val) => {
                          setFormData(prev => ({ ...prev, problemDate: val }));
                          if (errors.problemDate) {
                            setErrors(prev => ({ ...prev, problemDate: '' }));
                          }
                        }}
                        max={getToday()}
                        showTimeSelect={false}
                        error={!!errors.problemDate}
                        className="w-full"
                      />
                      {errors.problemDate && (
                        <p className="mt-1 text-sm text-red-600">{errors.problemDate}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 2: Reported Issue (full width) */}
              <div className="grid grid-cols-12 gap-6 mb-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label htmlFor="reportedIssue" className="block text-sm font-medium text-gray-700 mb-1">
                        Reported Issue <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-10">
                      <RichTextEditor
                        value={formData.reportedIssue || ''}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, reportedIssue: value }));
                          if (errors.reportedIssue) {
                            setErrors(prev => ({ ...prev, reportedIssue: '' }));
                          }
                        }}
                        placeholder="Describe the reported issue..."
                        minHeight="120px"
                      />
                      {errors.reportedIssue && (
                        <p className="mt-1 text-sm text-red-600">{errors.reportedIssue}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 3: Actual Service Impact (full width) */}
              <div className="grid grid-cols-12 gap-6 mb-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label htmlFor="actualServiceImpact" className="block text-sm font-medium text-gray-700 mb-1">
                        Actual Service Impact <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-10">
                      <RichTextEditor
                        value={formData.actualServiceImpact || ''}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, actualServiceImpact: value }));
                          if (errors.actualServiceImpact) {
                            setErrors(prev => ({ ...prev, actualServiceImpact: '' }));
                          }
                        }}
                        placeholder="Describe the actual service impact..."
                        minHeight="120px"
                      />
                      {errors.actualServiceImpact && (
                        <p className="mt-1 text-sm text-red-600">{errors.actualServiceImpact}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 4: Brief Analysis (full width) */}
              <div className="grid grid-cols-12 gap-6 mb-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label htmlFor="briefAnalysis" className="block text-sm font-medium text-gray-700 mb-1">
                        Brief Analysis <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-10">
                      <RichTextEditor
                        value={formData.briefAnalysis || ''}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, briefAnalysis: value }));
                          if (errors.briefAnalysis) {
                            setErrors(prev => ({ ...prev, briefAnalysis: '' }));
                          }
                        }}
                        placeholder="Provide a brief analysis..."
                        minHeight="120px"
                      />
                      {errors.briefAnalysis && (
                        <p className="mt-1 text-sm text-red-600">{errors.briefAnalysis}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 5: Root Cause (full width) */}
              <div className="grid grid-cols-12 gap-6 mb-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label htmlFor="rootCause" className="block text-sm font-medium text-gray-700 mb-1">
                        Root Cause <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-10">
                      <RichTextEditor
                        value={formData.rootCause || ''}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, rootCause: value }));
                          if (errors.rootCause) {
                            setErrors(prev => ({ ...prev, rootCause: '' }));
                          }
                        }}
                        placeholder="Describe the root cause of the problem..."
                        minHeight="120px"
                      />
                      {errors.rootCause && (
                        <p className="mt-1 text-sm text-red-600">{errors.rootCause}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 6: Detailed Analysis (full width) */}
              <div className="grid grid-cols-12 gap-6 mb-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label htmlFor="detailedAnalysis" className="block text-sm font-medium text-gray-700 mb-1">
                        Detailed Analysis <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-10">
                      <RichTextEditor
                        value={formData.detailedAnalysis || ''}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, detailedAnalysis: value }));
                          if (errors.detailedAnalysis) {
                            setErrors(prev => ({ ...prev, detailedAnalysis: '' }));
                          }
                        }}
                        placeholder="Provide detailed analysis..."
                        minHeight="150px"
                      />
                      {errors.detailedAnalysis && (
                        <p className="mt-1 text-sm text-red-600">{errors.detailedAnalysis}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 6: Corrective/Mitigate Actions (full width) */}
              <div className="grid grid-cols-12 gap-6 mb-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label htmlFor="correctiveActions" className="block text-sm font-medium text-gray-700 mb-1">
                        Corrective/Mitigate Actions <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-10">
                      <RichTextEditor
                        value={formData.correctiveActions || ''}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, correctiveActions: value }));
                          if (errors.correctiveActions) {
                            setErrors(prev => ({ ...prev, correctiveActions: '' }));
                          }
                        }}
                        placeholder="Describe corrective or mitigating actions..."
                        minHeight="120px"
                      />
                      {errors.correctiveActions && (
                        <p className="mt-1 text-sm text-red-600">{errors.correctiveActions}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 7: Chronology of Events (full width) */}
              <div className="grid grid-cols-12 gap-6 mb-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label htmlFor="chronologyOfEvents" className="block text-sm font-medium text-gray-700 mb-1">
                        Chronology of Events <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-10">
                      <RichTextEditor
                        value={formData.chronologyOfEvents || ''}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, chronologyOfEvents: value }));
                          if (errors.chronologyOfEvents) {
                            setErrors(prev => ({ ...prev, chronologyOfEvents: '' }));
                          }
                        }}
                        placeholder="Describe the chronology of events..."
                        minHeight="150px"
                      />
                      {errors.chronologyOfEvents && (
                        <p className="mt-1 text-sm text-red-600">{errors.chronologyOfEvents}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate(`/problems/${id}`)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateProblemMutation.isPending}
                className="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {updateProblemMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Assignment Group Change Confirmation Modal */}
      <AssignmentGroupChangeConfirmModal
        isOpen={groupChangeConfirmModal.isOpen}
        onConfirm={handleConfirmGroupChange}
        onCancel={handleCancelGroupChange}
        newGroupName={groupChangeConfirmModal.newGroupName}
        ticketNumber={problem?.number}
      />
    </div>
  );
};

export default ProblemEdit;